import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

import { StatusBadge } from "../../../components/ui/StatusBadge";
import { Checkbox } from "../../../components/ui/checkbox";
import { formatDateRange } from "../../../utils/dates";

/**
 * Warehouse "New Orders" tab table.
 *
 * Renders orders currently at `confirmed`, with a leading checkbox for
 * multiselect and a per-row "Acknowledge" (Bestätigen) action. Bulk
 * actions live in the parent page (`OrdersPage`) above/below the table.
 */
export function NewOrdersTable({
  orders,
  selectedIds,
  onToggleOne,
  onToggleAllOnPage,
  onAcknowledgeOne,
  ackBusyIds = new Set(),
  showUserColumn = false,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const currentPageIds = orders.map((o) => o.id);
  const allOnPageSelected =
    currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));

  return (
    <div
      className="overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-card"
      data-testid="new-orders-table-wrapper"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="new-orders-table">
          <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="w-10 px-4 py-3">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={onToggleAllOnPage}
                  data-testid="new-orders-select-all"
                  aria-label={t("new_orders.select_all_aria")}
                />
              </th>
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.order_number")}
              </th>
              {showUserColumn && (
                <th className="px-4 py-3 font-semibold">
                  {t("orders.columns.user")}
                </th>
              )}
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.event")}
              </th>
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.period")}
              </th>
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.status")}
              </th>
              <th className="px-4 py-3 text-right font-semibold">
                {t("orders.columns.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {orders.map((o) => (
              <tr
                key={o.id}
                onClick={() => navigate(`/orders/${o.id}`)}
                data-testid={`new-order-row-${o.id}`}
                className="cursor-pointer transition-colors hover:bg-offwhite/60"
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(o.id)}
                    onCheckedChange={() => onToggleOne(o.id)}
                    data-testid={`new-orders-select-${o.id}`}
                    aria-label={t("new_orders.select_row_aria")}
                  />
                </td>
                <td className="px-4 py-3">
                  <span
                    className="font-mono text-sm font-semibold text-text-primary"
                    data-testid={`new-order-row-number-${o.id}`}
                  >
                    {o.order_number ? `#${o.order_number}` : ""}
                  </span>
                </td>
                {showUserColumn && (
                  <td className="px-4 py-3 text-text-primary">
                    <span
                      className="font-medium"
                      title={o.user?.email || ""}
                    >
                      {o.user?.name || o.user?.email || "—"}
                    </span>
                  </td>
                )}
                <td
                  className="max-w-[320px] truncate px-4 py-3 text-text-primary"
                  title={o.event_name}
                >
                  {o.event_name}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                  {formatDateRange(o.from_date, o.until_date, i18n.language)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={o.status} size="sm" />
                </td>
                <td
                  className="px-4 py-3 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => onAcknowledgeOne(o)}
                    disabled={ackBusyIds.has(o.id)}
                    data-testid={`new-order-ack-${o.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-charcoal px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-charcoal-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne disabled:opacity-60 disabled:hover:bg-charcoal"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    {t("new_orders.acknowledge")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
